import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as api from "../lib/api";
import { Chat, type UiMessage, type UiBlock, type CutoffReason, type PendingAttachment } from "./Chat";
import { FileTree } from "./FileTree";
import { Preview } from "./Preview";
import { ShareModal } from "./ShareModal";
import { AppDetailsModal } from "./AppDetailsModal";
import type { AnthropicMessage, AnthropicContentBlock, ToolDispatchResult } from "../lib/agentTypes";
import { getModel, getReasoning } from "../lib/store";

export function Workspace({ projectId, onBack, onOpenSettings }: { projectId: string; onBack: () => void; onOpenSettings: () => void }) {
  const [project, setProject] = useState<api.Project | null>(null);
  const [files, setFiles] = useState<api.ProjectFile[]>([]);
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [messages, setMessages] = useState<UiMessage[]>([]);
  // Anthropic-format history (persisted to backend, used for agent runs)
  const [history, setHistory] = useState<AnthropicMessage[]>([]);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewKey, setPreviewKey] = useState(0);
  const [previewMode, setPreviewMode] = useState<"preview" | "deployed">("preview");
  const [deployStatus, setDeployStatus] = useState<{ at: number; versionId: string } | null>(null);
  const [tokenUsage, setTokenUsage] = useState<{ input: number; output: number } | null>(null);
  // Credit balance (USD) for a credit user — shown live in the topbar, updated from the run's
  // `credit` SSE events. Null = not a credit user (BYO key) / unknown.
  const [creditBalanceUsd, setCreditBalanceUsd] = useState<number | null>(null);
  // Transient toast shown when an auto-top-up fires mid-build.
  const [topupToast, setTopupToast] = useState<string | null>(null);
  const [shareOpen, setShareOpen] = useState(false);
  // App Details editor (name + blurb + tags). `detailsHint` carries a nudge when it's
  // auto-opened because a deploy was rejected for a missing name.
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsHint, setDetailsHint] = useState<string | null>(null);
  const [cutoff, setCutoff] = useState<{ reason: CutoffReason; message?: string } | null>(null);
  // Which pane is visible on mobile (single-pane layout below 768px). Default Preview
  // because once an app is built that's what you want to interact with; the agent's
  // streaming response is also visible since Chat is one tap away.
  const [mobileTab, setMobileTab] = useState<"chat" | "files" | "preview">("preview");
  // Desktop only: the Files pane is collapsed by default (it's mostly for sanity-checking),
  // and pops out when toggled. Doesn't affect mobile, where Files is its own tab.
  const [filesOpen, setFilesOpen] = useState(false);
  /** Set when getProject returns 403 — the project exists on the server but the current
   *  account doesn't own it. With OAuth, the recovery path is "sign in with the account
   *  that owns this project" (or it genuinely belongs to someone else). Replaces the old
   *  per-project reclaim hack, which let any cookie grab any project. */
  const [forbidden, setForbidden] = useState(false);
  // The builder loop runs server-side now. We track the active run's id (to POST Stop) and
  // a closer for its SSE stream.
  const runIdRef = useRef<string | null>(null);
  const streamCloseRef = useRef<(() => void) | null>(null);

  const refreshFiles = useCallback(async () => {
    try {
      const r = await api.listFiles(projectId);
      setFiles(r.files);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [projectId]);

  // Re-sync the whole conversation (messages + history + files) from the server's persisted
  // truth. Used after a reconnect, where we only streamed the live tail — this heals any
  // partial-streaming artifacts so the rendered state matches what's on disk.
  const reloadConversation = useCallback(async () => {
    try {
      const r = await api.getProject(projectId);
      setProject(r.project);
      setFiles(r.files);
      const { ui, hist } = buildUiFromChat(r.chat);
      setMessages(ui);
      setHistory(hist);
    } catch {
      // non-fatal — keep what's on screen
    }
  }, [projectId]);

  // Stream a server run's events into the chat UI. Shared by a fresh send (fromSeq 0) and a
  // reconnect to an in-flight run (fromSeq = lastSeq + 1 → live tail only). The server owns
  // chat persistence now, so this never writes chat back — it only renders.
  const consumeRun = useCallback(
    (
      runId: string,
      fromSeq: number,
      opts: { reconnect: boolean; initialMessages: UiMessage[]; initialHistory: AnthropicMessage[] }
    ) => {
      runIdRef.current = runId;
      setRunning(true);

      let live: UiMessage[] = opts.initialMessages;
      let finalHistory: AnthropicMessage[] = opts.initialHistory;
      let doneExit: string | null = null;
      const state: { activeAsst: UiMessage | null } = { activeAsst: null };
      const toolBlocks = new Map<string, Extract<UiBlock, { type: "tool_call" }>>();

      const updateMessages = (next: UiMessage[]): void => {
        live = next;
        setMessages(next);
      };
      const newAsst = (): UiMessage => ({
        id: "a" + Math.random().toString(36).slice(2, 8),
        kind: "assistant",
        complete: false,
        blocks: [],
        ts: Date.now(),
      });
      // Lazily start an assistant bubble if events arrive without an assistant_start (can
      // happen on reconnect, where the turn's start preceded our fromSeq).
      const ensureActive = (): UiMessage => {
        if (state.activeAsst) return state.activeAsst;
        const a = newAsst();
        state.activeAsst = a;
        updateMessages([...live, a]);
        return a;
      };

      const close = api.streamRun(projectId, runId, fromSeq, {
        onEvent: (ev) => {
          const d = (ev.data ?? {}) as Record<string, any>;
          switch (ev.type) {
            case "assistant_start": {
              const a = newAsst();
              state.activeAsst = a;
              updateMessages([...live, a]);
              break;
            }
            case "text": {
              const a = ensureActive();
              const last = a.blocks[a.blocks.length - 1];
              if (last && last.type === "text") last.text += d.delta;
              else a.blocks.push({ type: "text", text: d.delta });
              updateMessages([...live]);
              break;
            }
            case "tool_use": {
              const a = ensureActive();
              const block: Extract<UiBlock, { type: "tool_call" }> = {
                type: "tool_call",
                id: d.id,
                name: d.name,
                input: d.input,
              };
              a.blocks.push(block);
              toolBlocks.set(d.id, block);
              updateMessages([...live]);
              break;
            }
            case "tool_result": {
              const block = toolBlocks.get(d.toolUseId);
              if (block) block.result = d.result as ToolDispatchResult;
              updateMessages([...live]);
              if (!d.result?.is_error) {
                void refreshFiles();
                setPreviewKey((k) => k + 1);
                const FILE_MUTATORS = new Set(["write_file", "edit_file", "delete_file"]);
                if (block && FILE_MUTATORS.has(block.name)) {
                  setProject((p) => (p ? { ...p, lastFileWriteAt: Date.now() } : p));
                }
                if (block?.name === "set_project_name") {
                  void api.getProject(projectId).then((r) => setProject(r.project)).catch(() => {});
                }
              }
              break;
            }
            case "assistant_message": {
              const msg = d.message as AnthropicMessage;
              finalHistory = [...finalHistory, msg];
              const fullText = Array.isArray(msg.content)
                ? msg.content.filter((b) => b.type === "text").map((b) => (b as { type: "text"; text: string }).text).join("")
                : "";
              // Replace streamed text with the authoritative message text (heals partial
              // streaming) and mark complete — but KEEP it as activeAsst so subsequent
              // tool_use events for THIS turn attach to the same bubble (the loop emits
              // tool_use AFTER assistant_message). The next turn's assistant_start swaps
              // activeAsst out. (Earlier code nulled activeAsst here, which caused tool_use
              // to spawn a fresh incomplete bubble whose spinner kept animating forever.)
              const a = state.activeAsst ?? newAsst();
              const toolCalls = a.blocks.filter((b) => b.type === "tool_call");
              a.blocks = fullText ? [{ type: "text", text: fullText }, ...toolCalls] : [...toolCalls];
              a.complete = true;
              if (!state.activeAsst) {
                // reconnect: no bubble existed yet — push the new one and adopt it as active.
                state.activeAsst = a;
                updateMessages([...live, a]);
              } else {
                updateMessages([...live]);
              }
              break;
            }
            case "user_message": {
              // Tool_result user-turns attach to existing tool_call cards (handled by
              // tool_result events). The initial user text bubble is added synchronously by
              // onSend (fresh) or already in the persisted chat (reconnect). So we only need
              // to keep history in sync here.
              finalHistory = [...finalHistory, d.message as AnthropicMessage];
              break;
            }
            case "usage": {
              setTokenUsage({ input: d.input_tokens, output: d.output_tokens });
              break;
            }
            case "credit": {
              // Live spend/top-up updates. Keep the topbar balance current; on a top-up, show
              // a toast AND drop a user-only note into the stream (the server also persists it,
              // so it's still there on reload — crystal clear about spend).
              if (typeof d.balanceMicro === "number") setCreditBalanceUsd(d.balanceMicro / 1e6);
              if (d.kind === "topup") {
                const toppedUsd = (d.toppedMicro ?? 0) / 1e6;
                const balanceUsd = (d.balanceMicro ?? 0) / 1e6;
                const text = topupNoticeText(toppedUsd, balanceUsd);
                setTopupToast(text);
                updateMessages([...live, { id: "sys" + Math.random().toString(36).slice(2, 8), kind: "system", complete: true, blocks: [{ type: "text", text }], ts: Date.now() }]);
              } else if (d.kind === "topup_failed") {
                const balanceUsd = (d.balanceMicro ?? 0) / 1e6;
                const text = topupFailedText(d.error ?? "unknown error", balanceUsd);
                setTopupToast(text);
                updateMessages([...live, { id: "sys" + Math.random().toString(36).slice(2, 8), kind: "system", complete: true, blocks: [{ type: "text", text }], ts: Date.now() }]);
              }
              break;
            }
            case "error": {
              setError(d.message);
              break;
            }
            case "done": {
              doneExit = (d.exitReason as string) ?? null;
              break;
            }
          }
        },
        onError: (err) => setError(err.message),
        onClose: () => {
          streamCloseRef.current = null;
          runIdRef.current = null;
          if (state.activeAsst && !state.activeAsst.complete) {
            state.activeAsst.complete = true;
            updateMessages([...live]);
          }
          setHistory(finalHistory);
          setRunning(false);
          if (doneExit && doneExit !== "natural" && doneExit !== "aborted") {
            setCutoff({ reason: doneExit as CutoffReason });
          }
          // After a reconnect we only saw the live tail — reconcile with persisted truth.
          if (opts.reconnect) void reloadConversation();
        },
      });
      streamCloseRef.current = close;
    },
    [projectId, refreshFiles, reloadConversation]
  );

  // Close any open SSE stream on unmount. The run keeps going server-side (that's the point);
  // we just stop listening. Re-opening the project reconnects via getActiveRun.
  useEffect(() => () => streamCloseRef.current?.(), []);

  // Seed the credit balance for the topbar pill (credit users only; ignored for guests/BYO).
  useEffect(() => {
    api.getAccountCredits().then((c) => setCreditBalanceUsd(c.balanceUsd)).catch(() => {});
  }, []);

  // Auto-dismiss the top-up toast.
  useEffect(() => {
    if (!topupToast) return;
    const t = setTimeout(() => setTopupToast(null), 6000);
    return () => clearTimeout(t);
  }, [topupToast]);

  // Initial load
  useEffect(() => {
    (async () => {
      try {
        const r = await api.getProject(projectId);
        setProject(r.project);
        setFiles(r.files);
        if (!selectedFile && r.files.length) setSelectedFile(r.files[0]!.path);

        // Reconstruct UI + history from saved chat.
        const { ui, hist } = buildUiFromChat(r.chat);
        setMessages(ui);
        setHistory(hist);

        try {
          const cv = await api.getCurrentVersion(projectId);
          setDeployStatus({ at: cv.version.createdAt, versionId: cv.version.id });
        } catch {
          // never deployed — fine
        }

        // Resume an in-flight server run (the "survives a tab close" payoff). The completed
        // turns are already rendered from persisted chat above; stream only the live tail
        // (events after lastSeq), then reconcile on close.
        let resuming = false;
        try {
          const active = await api.getActiveRun(projectId);
          if (active.run) {
            resuming = true;
            setCutoff(null);
            consumeRun(active.run.runId, active.run.lastSeq + 1, {
              reconnect: true,
              initialMessages: ui,
              initialHistory: hist,
            });
          }
        } catch {
          // no active run / transient — fall through
        }

        // Detect an interrupted run from a previous session (only when nothing is live). If
        // the persisted history ends with a user message containing tool_results (the agent
        // finished a turn's tools and was about to think again) but no assistant message
        // followed, the run died. Surface a Continue banner.
        if (!resuming) {
          const lastMsg = hist[hist.length - 1];
          const lastIsToolResults =
            lastMsg?.role === "user" &&
            Array.isArray(lastMsg.content) &&
            lastMsg.content.some((b) => b.type === "tool_result");
          if (lastIsToolResults) {
            setCutoff({
              reason: "error",
              message: "Last run was interrupted before the agent finished. Click Continue to resume.",
            });
          }
        }
      } catch (e) {
        const msg = (e as Error).message ?? "";
        // "forbidden" is the literal error string returned by the backend for the
        // ownership-mismatch case. Promote it to a claim CTA instead of a generic
        // error so the user has a self-service recovery path.
        if (/forbidden/i.test(msg)) {
          setForbidden(true);
        } else {
          setError(msg);
        }
      }
    })();
  }, [projectId]);

  const onAttach = useCallback(
    async ({ name, content, deployed }: { name: string; content: string; deployed: boolean }): Promise<PendingAttachment> => {
      // Defensive: sanitize filename (the modal already does basic normalization but trust nothing from a text input).
      const safe = name.replace(/[\\/]+/g, "-").replace(/[^A-Za-z0-9._-]/g, "_").replace(/^\.+/, "").slice(0, 80) || "attachment.txt";
      // `assets/<name>` is bundled with the deploy (snapshotVersion only excludes
      // `attachments/*`). `attachments/<name>` is build-time-only reference material.
      const projectPath = deployed ? `assets/${safe}` : `attachments/${safe}`;
      await api.writeFile(projectId, projectPath, content);
      await refreshFiles();
      return { path: projectPath, size: new Blob([content]).size };
    },
    [projectId, refreshFiles]
  );

  const onSend = useCallback(
    async (userText: string, attachments: PendingAttachment[]) => {
      setError(null);
      setRunning(true);
      setCutoff(null); // clear any prior cutoff on a new send

      // Prepend attachment markers so the agent sees them as part of the user message.
      // Format mirrors the file references in the system prompt — agent reads via read_file
      // (or, for assets/*, fetches at runtime from the deployed app).
      let augmented = userText;
      if (attachments.length > 0) {
        const markers = attachments
          .map((a) =>
            a.path.startsWith("assets/")
              ? `[Attached file: ${a.path} (${a.size} bytes) — BUNDLED with the deployed app. Fetch at runtime: \`await fetch("./${a.path}")\`. Do NOT embed its content inline; let the runtime load it.]`
              : `[Attached file: ${a.path} (${a.size} bytes) — reference material, NOT in the deployed bundle. Read it with read_file at build time; embed any content you actually need into your code.]`
          )
          .join("\n");
        augmented = markers + (userText ? "\n\n" + userText : "");
      }

      // Add the user UI bubble synchronously — show the typed text (plus a small attachment
      // indicator). The server persists the canonical message; this is just the local echo.
      const ts = Date.now();
      const userUi: UiMessage = {
        id: "u" + ts,
        kind: "user",
        complete: true,
        blocks: [
          {
            type: "text",
            text:
              (attachments.length > 0
                ? attachments.map((a) => `📎 ${a.path}`).join("\n") + (userText ? "\n\n" : "")
                : "") + userText,
          },
        ],
        ts,
      };
      const startMessages = [...messages, userUi];
      setMessages(startMessages);

      // Start the run server-side. The loop runs on the backend (survives a tab close); we
      // stream its events back over SSE. The API key is resolved server-side from the
      // signed-in account — never sent from here.
      try {
        const { runId } = await api.startRun(projectId, {
          userText: augmented,
          model: getModel(),
          reasoning: getReasoning(),
        });
        consumeRun(runId, 0, { reconnect: false, initialMessages: startMessages, initialHistory: history });
      } catch (e) {
        const msg = (e as Error).message ?? "";
        // Not signed in / no key on file → send them to Settings to set it up.
        if (/api key|signed in/i.test(msg)) {
          setRunning(false);
          onOpenSettings();
          return;
        }
        // A run is already active for this project (e.g. another tab started one) → attach
        // to it rather than erroring.
        if (/already active/i.test(msg)) {
          try {
            const active = await api.getActiveRun(projectId);
            if (active.run) {
              consumeRun(active.run.runId, active.run.lastSeq + 1, {
                reconnect: true,
                initialMessages: startMessages,
                initialHistory: history,
              });
              return;
            }
          } catch {
            // fall through to surfacing the error
          }
        }
        setRunning(false);
        setError(msg);
        setCutoff({ reason: "error", message: msg });
      }
    },
    [history, messages, projectId, onOpenSettings, consumeRun]
  );

  function onAbort() {
    const runId = runIdRef.current;
    if (!runId) return;
    // Tell the server to abort the run. It checks the signal between turns / mid-stream and
    // emits a terminal done(aborted), which closes our SSE stream and flips `running` off.
    void api.stopRun(projectId, runId).catch(() => {});
  }

  async function onDeploy() {
    setError(null);
    try {
      const r = await api.deploy(projectId);
      setDeployStatus({ at: r.version.createdAt, versionId: r.version.id });
      // First deploy allocates the URL slug — fold it into local state so the Share modal
      // shows the pretty /<username>/<slug> URL immediately, without a page reload.
      if (r.slug) setProject((p) => (p ? { ...p, slug: r.slug } : p));
      setPreviewKey((k) => k + 1);
    } catch (e) {
      // The backend rejects deploys with no real name. Open the details editor to fix it,
      // rather than surfacing a raw error.
      if ((e as Error).message === "name_required") {
        setDetailsHint("Give your app a name (and a quick pitch) so people can find it, then deploy.");
        setDetailsOpen(true);
        return;
      }
      setError((e as Error).message);
    }
  }

  async function onClearChat() {
    if (!confirm("Clear the chat history? Files are kept.")) return;
    await api.clearChat(projectId);
    setMessages([]);
    setHistory([]);
  }

  async function onRename() {
    const next = prompt("New name", project?.name ?? "");
    if (!next || !project) return;
    await api.renameProject(projectId, next);
    setProject({ ...project, name: next });
  }

  const deployUrl = `${location.origin}/a/${projectId}`;
  // Dirty = working copy has writes newer than the current deployed version. Server is
  // the source of truth for both timestamps; we just compare them.
  const isDirty = useMemo(() => {
    if (!project) return false;
    if (project.lastFileWriteAt === 0) return false; // never written = nothing to deploy
    if (!deployStatus) return true; // has files but never deployed → pending
    return project.lastFileWriteAt > deployStatus.at;
  }, [project, deployStatus]);
  const shortStatus = useMemo(() => {
    if (!deployStatus) return "never deployed";
    if (isDirty) return "changes since deploy";
    const s = Math.floor((Date.now() - deployStatus.at) / 1000);
    return s < 60 ? `deployed ${s}s ago` : `deployed ${Math.floor(s / 60)}m ago`;
  }, [deployStatus, isDirty]);

  if (forbidden) {
    return (
      <div className="shell">
        <div className="topbar">
          <button className="btn-ghost" onClick={onBack}>← back</button>
          <div className="title">Project locked</div>
        </div>
        <div style={{ maxWidth: 520, margin: "60px auto", padding: 20, textAlign: "center" }}>
          <h2 style={{ marginTop: 0 }}>This project belongs to a different account</h2>
          <p style={{ color: "var(--fg-dim)", marginBottom: 18 }}>
            You're not signed in as the account that owns this project. If it's yours,
            sign in with the same account you used to create it — your apps follow you
            across devices once you're signed in.
          </p>
          <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
            <button className="primary" onClick={() => (window.location.href = "/auth/google/start")}>
              Sign in with Google
            </button>
            <button className="btn-ghost" onClick={onBack}>Back to projects</button>
          </div>
          {error && (
            <div className="status-err mono" style={{ marginTop: 14, fontSize: 12 }}>
              {error}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="shell">
      <div className="topbar">
        <button className="btn-ghost" onClick={onBack}>
          ← back
        </button>
        <div className="title" onClick={onRename} style={{ cursor: "pointer" }} title="Click to rename">
          {project?.name ?? "Loading…"}
        </div>
        <div className="mono hide-on-mobile" style={{ color: "var(--fg-faint)", fontSize: 11 }}>
          {projectId}
        </div>
        <div className="spacer" />
        {creditBalanceUsd != null && (
          <span
            className="mono"
            style={{ fontSize: 12, fontWeight: 600, color: creditBalanceUsd <= 1 ? "var(--err)" : "var(--fg-dim)" }}
            title="Your prepaid credit balance (updates live as a build spends)"
          >
            ${creditBalanceUsd.toFixed(2)}
          </span>
        )}
        {tokenUsage && (
          <span
            className="mono hide-on-mobile"
            style={{ color: "var(--fg-faint)", fontSize: 11 }}
            title="cumulative tokens this session"
          >
            {tokenUsage.input}↓ · {tokenUsage.output}↑
          </span>
        )}
        <span
          className={"mono hide-on-mobile " + (isDirty ? "status-warn" : "")}
          style={{ fontSize: 11, color: isDirty ? undefined : "var(--fg-faint)" }}
          title={isDirty ? "Working copy has changes that aren't deployed yet" : "Working copy matches deployed version"}
        >
          {isDirty && "● "}
          {shortStatus}
        </span>
        <button
          className={"btn-ghost hide-on-mobile" + (filesOpen ? " primary" : "")}
          onClick={() => setFilesOpen((v) => !v)}
          title="Toggle the files pane (mostly for sanity-checking what the agent wrote)"
        >
          Files
        </button>
        <button
          className="btn-ghost hide-on-mobile"
          onClick={() => { setDetailsHint(null); setDetailsOpen(true); }}
          title="Edit name, pitch, and tags (how the app shows up in the hub)"
        >
          Details
        </button>
        <button onClick={() => setShareOpen(true)} title="Share app: name, URL, big QR code">
          Share
        </button>
        <button
          className={"primary " + (isDirty ? "deploy-pending" : "")}
          onClick={onDeploy}
          disabled={files.length === 0}
          title={
            isDirty
              ? "Deploy the working copy — public URL still shows the previous version"
              : "Public version is up to date"
          }
        >
          Deploy{isDirty ? " ↑" : ""}
        </button>
        <button
          className="btn-ghost hide-on-mobile"
          onClick={() => api.exportProject(projectId, project?.name).catch((e) => setError((e as Error).message))}
          title="Download this project as a zip (files + chat). Re-import it from any Quikpl deployment."
        >
          ⬇ Export
        </button>
        <button className="btn-ghost" onClick={onOpenSettings} title="Settings">⚙</button>
      </div>
      {shareOpen && (
        <ShareModal
          projectId={projectId}
          appName={project?.name ?? "Untitled app"}
          slug={project?.slug ?? null}
          onClose={() => setShareOpen(false)}
        />
      )}
      {detailsOpen && project && (
        <AppDetailsModal
          project={project}
          hint={detailsHint}
          onClose={() => { setDetailsOpen(false); setDetailsHint(null); }}
          onSaved={({ name, description, tags }) => {
            setProject((p) => (p ? { ...p, name, description, tags } : p));
            setDetailsOpen(false);
            setDetailsHint(null);
          }}
        />
      )}
      {error && (
        <div className="mono status-err" style={{ padding: 8, borderBottom: "1px solid var(--border)" }}>
          {error}
        </div>
      )}
      <div className={"workspace" + (filesOpen ? " files-open" : "")} data-active={mobileTab}>
        <Chat
          messages={messages}
          running={running}
          onSend={onSend}
          onAttach={onAttach}
          onAbort={onAbort}
          onClear={onClearChat}
          cutoff={cutoff}
          onContinue={() => onSend("Please continue from where you left off.", [])}
        />
        <FileTree files={files} selected={selectedFile} onSelect={setSelectedFile} />
        <div className="pane last" style={{ flexDirection: "column" }}>
          <div className="pane-header" style={{ display: "flex", justifyContent: "space-between" }}>
            <span>Preview</span>
            <div style={{ display: "flex", gap: 4 }}>
              <button
                className={"btn-ghost" + (previewMode === "preview" ? " primary" : "")}
                style={{ fontSize: 11, padding: "1px 8px" }}
                onClick={() => setPreviewMode("preview")}
              >
                working copy
              </button>
              <button
                className={"btn-ghost" + (previewMode === "deployed" ? " primary" : "")}
                style={{ fontSize: 11, padding: "1px 8px" }}
                onClick={() => setPreviewMode("deployed")}
                disabled={!deployStatus}
              >
                deployed
              </button>
              <span style={{ width: 6 }} />
              <button
                className="btn-ghost"
                style={{ fontSize: 11, padding: "1px 8px" }}
                title="Open a multi-player test view in a new window (4 panes, each with a distinct test identity)"
                onClick={() =>
                  window.open(
                    `/a/${projectId}/multi?n=4&preview=${previewMode === "preview" ? 1 : 0}`,
                    "_blank",
                    "width=1400,height=900"
                  )
                }
              >
                ⊞ multi
              </button>
            </div>
          </div>
          <PreviewIframe projectId={projectId} previewKey={previewKey} previewMode={previewMode} />
        </div>
      </div>
      {/* Mobile-only tab bar — hidden on desktop via the .mobile-tabs media query. */}
      <nav className="mobile-tabs" role="tablist" aria-label="Workspace panes">
        <button
          className={mobileTab === "chat" ? "active" : ""}
          onClick={() => setMobileTab("chat")}
          role="tab"
          aria-selected={mobileTab === "chat"}
        >
          <span className="ico" aria-hidden>💬</span>
          <span className="lbl">Chat</span>
        </button>
        <button
          className={mobileTab === "files" ? "active" : ""}
          onClick={() => setMobileTab("files")}
          role="tab"
          aria-selected={mobileTab === "files"}
        >
          <span className="ico" aria-hidden>📁</span>
          <span className="lbl">Files</span>
        </button>
        <button
          className={mobileTab === "preview" ? "active" : ""}
          onClick={() => setMobileTab("preview")}
          role="tab"
          aria-selected={mobileTab === "preview"}
        >
          <span className="ico" aria-hidden>📱</span>
          <span className="lbl">Preview</span>
        </button>
      </nav>
      {topupToast && (
        <div
          role="status"
          onClick={() => setTopupToast(null)}
          style={{
            position: "fixed",
            top: 16,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 200,
            background: "var(--panel)",
            border: "1px solid var(--accent, #f5a623)",
            color: "var(--fg)",
            borderRadius: 8,
            padding: "8px 14px",
            fontSize: 13,
            boxShadow: "0 6px 24px rgba(0,0,0,0.4)",
            cursor: "pointer",
          }}
        >
          {topupToast}
        </div>
      )}
    </div>
  );
}

/**
 * Wraps the preview iframe with a dark overlay that covers the white-flash that happens
 * while the iframe reloads after a file change. The overlay fades out shortly after the
 * iframe's `load` event (we add a small delay so the inner sandboxed iframe inside the
 * wrapper has time to paint too).
 */
function PreviewIframe({
  projectId,
  previewKey,
  previewMode,
}: {
  projectId: string;
  previewKey: number;
  previewMode: "preview" | "deployed";
}) {
  const [reloading, setReloading] = useState(false);
  const safetyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Trigger reload state on every key/mode change.
  useEffect(() => {
    setReloading(true);
    if (safetyTimer.current) clearTimeout(safetyTimer.current);
    // Fallback in case load event never fires (e.g. 404 inner). Cap at 1.5s.
    safetyTimer.current = setTimeout(() => setReloading(false), 1500);
    return () => {
      if (safetyTimer.current) clearTimeout(safetyTimer.current);
    };
  }, [previewKey, previewMode]);

  const onLoad = () => {
    // The wrapper's `load` fires before its inner sandboxed iframe is fully painted.
    // A short delay covers that gap so we don't fade to expose a half-rendered app.
    setTimeout(() => setReloading(false), 220);
  };

  return (
    <div className="preview" style={{ position: "relative" }}>
      <iframe
        key={previewKey + ":" + previewMode}
        src={previewMode === "preview" ? `/a/${projectId}?preview=1` : `/a/${projectId}`}
        title="preview"
        onLoad={onLoad}
      />
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          background: "#0b0e14",
          pointerEvents: "none",
          opacity: reloading ? 1 : 0,
          transition: "opacity 220ms ease-out",
        }}
      />
    </div>
  );
}

// Copy for an auto-top-up interjection (used both for the live event and the persisted record).
function topupNoticeText(toppedUsd: number, balanceUsd: number): string {
  return `💳 Auto top-up: added $${toppedUsd.toFixed(2)} in credits — balance now $${balanceUsd.toFixed(2)}.`;
}
function topupFailedText(error: string, balanceUsd: number): string {
  return `⚠️ Auto-top-up failed (${error}). Balance is $${balanceUsd.toFixed(2)} — add credits or update your payment method to keep building.`;
}

// Rebuild the chat UI + Anthropic-format history from the server's persisted chat. Each
// persisted entry's `content` is a full Anthropic message ({ role, content }). User
// tool_result turns don't get their own bubble — they attach to the previous assistant's
// tool_call cards.
function buildUiFromChat(chat: api.ChatMessage[]): { ui: UiMessage[]; hist: AnthropicMessage[] } {
  const ui: UiMessage[] = [];
  const hist: AnthropicMessage[] = [];
  let seq = 0;
  for (const c of chat) {
    // "system" rows are user-only interjections (e.g. auto-top-up notices): render in the
    // stream, but NEVER add to the model history.
    if (c.role === "system") {
      const d = (c.content ?? {}) as { kind?: string; toppedUsd?: number; balanceUsd?: number; error?: string };
      const text =
        d.kind === "topup"
          ? topupNoticeText(d.toppedUsd ?? 0, d.balanceUsd ?? 0)
          : d.kind === "topup_failed"
            ? topupFailedText(d.error ?? "unknown error", d.balanceUsd ?? 0)
            : null;
      if (text) {
        ui.push({
          id: "sys" + seq++,
          kind: "system",
          complete: true,
          blocks: [{ type: "text", text }],
          ts: Date.now(),
        });
      }
      continue;
    }
    const entry = c.content as AnthropicMessage;
    hist.push(entry);
    if (entry.role === "user") {
      if (typeof entry.content === "string") {
        ui.push({ id: "m" + seq++, kind: "user", complete: true, blocks: [{ type: "text", text: entry.content }], ts: Date.now() });
      } else if (Array.isArray(entry.content)) {
        for (const block of entry.content) {
          if (block.type === "tool_result") {
            attachResultToLast(ui, block.tool_use_id, {
              is_error: !!block.is_error,
              content: block.content,
              display: derivedDisplay(block),
            });
          }
        }
      }
    } else {
      const blocks: UiBlock[] = [];
      for (const b of entry.content) {
        if (b.type === "text") blocks.push({ type: "text", text: b.text });
        else if (b.type === "tool_use") blocks.push({ type: "tool_call", id: b.id, name: b.name, input: b.input });
      }
      ui.push({ id: "m" + seq++, kind: "assistant", complete: true, blocks, ts: Date.now() });
    }
  }
  return { ui, hist };
}

function attachResultToLast(ui: UiMessage[], toolUseId: string, result: ToolDispatchResult): void {
  for (let i = ui.length - 1; i >= 0; i--) {
    const m = ui[i]!;
    if (m.kind !== "assistant") continue;
    for (const b of m.blocks) {
      if (b.type === "tool_call" && b.id === toolUseId) {
        b.result = result;
        return;
      }
    }
  }
}

// Derive a chat-UI display payload from a stored tool_result block.
function derivedDisplay(block: Extract<AnthropicContentBlock, { type: "tool_result" }>): ToolDispatchResult["display"] {
  if (typeof block.content === "string") {
    return { summary: shortSummary(block.content), text: block.content };
  }
  const text = block.content
    .filter((c) => c.type === "text")
    .map((c) => (c as { type: "text"; text: string }).text)
    .join("\n");
  const image = block.content.find((c) => c.type === "image") as
    | { type: "image"; source: { type: "base64"; media_type: "image/png"; data: string } }
    | undefined;
  return {
    summary: text ? shortSummary(text) : image ? "screenshot" : undefined,
    text: text || undefined,
    imageBase64: image?.source.data,
  };
}

function shortSummary(s: string): string {
  const first = s.split("\n")[0]!;
  return first.length > 80 ? first.slice(0, 77) + "…" : first;
}
